// ============================================================================
// USER BRANCH ACCESS HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { userAccessService } from 'api/services/userAccessService';
import type {
  UserBuAccess,
  AssignBuAccessRequest,
  UpdateBuAccessRequest,
  MyBranchItem,
  UserLookup,
  UserAccessFilterParams,
} from 'api/types/userAccess.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'user-access';

export const useUserAccess = (params?: UserAccessFilterParams) => {
  const [data, setData] = useState<UserBuAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserAccess = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userAccessService.getAll({ pageSize: 50000, ...params });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchUserAccess();
  }, [fetchUserAccess]);

  useDataChangeListener(DATA_KEY, fetchUserAccess);

  return { data, loading, error, refetch: fetchUserAccess };
};

export const useMyBranches = () => {
  const [data, setData] = useState<MyBranchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMyBranches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userAccessService.getMyBranches();
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyBranches();
  }, [fetchMyBranches]);

  useDataChangeListener(DATA_KEY, fetchMyBranches);

  return { data, loading, error, refetch: fetchMyBranches };
};

export const useUsersLookup = () => {
  const [data, setData] = useState<UserLookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsersLookup = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userAccessService.getUsersLookup();
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsersLookup();
  }, [fetchUsersLookup]);

  return { data, loading, error, refetch: fetchUsersLookup };
};

export const useAssignAccess = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const assignAccess = useCallback(async (payload: AssignBuAccessRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await userAccessService.assign(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { assignAccess, loading, error };
};

export const useUpdateAccess = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateAccess = useCallback(async (id: string, payload: UpdateBuAccessRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await userAccessService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateAccess, loading, error };
};

export const useRevokeAccess = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const revokeAccess = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await userAccessService.revoke(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { revokeAccess, loading, error };
};
